// src/pages/AcompanhamentoIdosos.js

import React, { useState, useEffect, useMemo } from 'react';
import { Search, User, Stethoscope, Scale, Syringe, ChevronLeft, ChevronRight, MapPin, Filter, XCircle, Clock, CheckCircle, Check, Square } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import StatusIndicator from '../components/StatusIndicator';
import FilterControls from '../components/FilterControls';
import MonitoringCharts from '../components/MonitoringCharts';
import { usePatientFilters } from '../hooks/usePatientFilters';
import { apiFetch } from '../utils/apiFetch';
import './Acompanhamento.css'; // green theme

// --- HELPERS (Padronização com as outras páginas) ---

const getAddressKey = (p) => {
    const rua = (p.endereco || 'ZZ_SEM_ENDERECO').trim().toLowerCase();
    const num = (p.numero || 'S/N').trim().toLowerCase();
    const comp = (p.complemento || '').trim().toLowerCase();
    return `${rua}|${num}|${comp}`;
};

const formatAddressDisplay = (p) => {
    if (!p.endereco) return "Endereço Não Informado";
    const rua = p.endereco.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
    const num = p.numero ? `, ${p.numero}` : '';
    const comp = p.complemento ? ` (${p.complemento})` : '';
    return `${rua}${num}${comp}`;
};

// Configuração dos Gráficos
const CHART_CONFIG_IDOSOS = [
    { id: 'visitasACS', label: 'Visitas ACS (2x/ano)', icon: <User size={16}/> },
    { id: 'consulta', label: 'Consulta (12m)', icon: <Stethoscope size={16}/> },
    { id: 'antropometria', label: 'Peso/Altura (12m)', icon: <Scale size={16}/> },
    { id: 'vacinaInfluenza', label: 'Vacina Influenza', icon: <Syringe size={16}/> },
];

// HEADER DA COLUNA INTELIGENTE
const ColumnHeader = ({ label, icon: Icon, filterState, onClick, width }) => {
    const isActive = filterState?.key; 
    const type = filterState?.type;

    let StatusIcon = Filter;
    let iconColor = "#94a3b8";

    if (isActive) {
        if (type === 'pending') {
            StatusIcon = XCircle;
            iconColor = "#ef4444";
        } else if (type === 'completed') {
            StatusIcon = CheckCircle;
            iconColor = "#22c55e";
        }
    }

    return (
        <div 
            className={`col-atividade header-clickable ${isActive ? 'active-filter' : ''}`} 
            onClick={onClick}
            title={isActive ? (type === 'pending' ? "Filtrando Pendentes" : "Filtrando Realizados") : "Filtrar Pendentes"}
            style={{ cursor: 'pointer', userSelect: 'none', width: width }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                <Icon size={14} />
                <span className="header-label-text">{label}</span>
                <StatusIcon size={12} fill={isActive ? "white" : "none"} color={iconColor} className={!isActive ? "filter-icon-hover" : ""} />
            </div>
        </div>
    );
};

export default function AcompanhamentoIdosos() {
    const navigate = useNavigate();
    const [idosos, setIdosos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    
    // Estado de Seleção
    const [selectedPatients, setSelectedPatients] = useState(new Set());

    const {
        searchTerm, setSearchTerm,
        sortConfig, setSortConfig,
        activeAlerts, setActiveAlerts,
        selectedMicroArea, setSelectedMicroArea,
        statusFilter, setStatusFilter,
        filteredList: filteredIdosos,
    } = usePatientFilters(
        idosos, 
        ['nome', 'cns', 'endereco'], 
        { key: 'endereco', direction: 'ascending' } 
    );

    const availableMicroAreas = useMemo(() => {
        const areas = new Set(idosos.map(p => p.micro_area).filter(Boolean));
        return Array.from(areas).sort();
    }, [idosos]);

    useEffect(() => { setCurrentPage(1); }, [searchTerm, activeAlerts, selectedMicroArea, statusFilter]);

    // Recuperar seleção salva
    useEffect(() => {
        const saved = localStorage.getItem('selectedIdosos');
        if (saved) {
            try { setSelectedPatients(new Set(JSON.parse(saved))); } catch (e) { console.error(e); }
        }
    }, []);

    useEffect(() => {
        const fetchPacientes = async () => {
            setLoading(true);
            try {
                // Batendo na nova rota super otimizada
                const response = await apiFetch('/api/v2/idosos'); 
                const result = await response.json();
                const allPatients = result.data || [];

                const formatarData = (dataSql) => {
                    if (!dataSql) return '';
                    const d = new Date(dataSql);
                    return new Date(d.getTime() + d.getTimezoneOffset() * 60000).toLocaleDateString('pt-BR');
                };

                const buildStatus = (statusBanco, dataAcao) => {
                    const emDia = statusBanco === 'EM_DIA';
                    return {
                        target: 1,
                        current: emDia ? 1 : 0,
                        status: emDia ? 'ok' : 'critical',
                        text: emDia ? `Em dia (${formatarData(dataAcao)})` : 'Pendente'
                    };
                };

                const idososData = allPatients.map(p => {
                    const statusAtualizado = {
                        visitasACS: buildStatus(p.status_visita, p.data_ultima_visita),
                        consulta: buildStatus(p.status_consulta, p.data_ultima_consulta),
                        antropometria: buildStatus(p.status_antropometria, p.data_ultima_antropometria),
                        vacinaInfluenza: buildStatus(p.status_vacina, p.data_ultima_vacina),
                        score: 0
                    };

                    // Pontuação Oficial da Nota Técnica C6 (25 pts cada)
                    if (p.status_visita === 'EM_DIA') statusAtualizado.score += 25;
                    if (p.status_consulta === 'EM_DIA') statusAtualizado.score += 25;
                    if (p.status_antropometria === 'EM_DIA') statusAtualizado.score += 25;
                    if (p.status_vacina === 'EM_DIA') statusAtualizado.score += 25;

                    return { ...p, status: statusAtualizado };
                });
                
                // Ordenação por idade e depois alfabética
                idososData.sort((a, b) => {
                    if (b.idade !== a.idade) return b.idade - a.idade;
                    return a.nome.localeCompare(b.nome);
                });

                setIdosos(idososData);
            } catch (error) {
                console.error("Erro ao buscar idosos da V2:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchPacientes();
    }, []);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredIdosos.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredIdosos.length / itemsPerPage);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
            document.querySelector('.acompanhamento-container')?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const getFilterState = (key) => statusFilter.key === key ? statusFilter : null;

    // --- LÓGICA DE SELEÇÃO E ENVIO ---

    const togglePatientSelection = (cns) => {
        setSelectedPatients(prev => {
            const newSet = new Set(prev);
            if (newSet.has(cns)) newSet.delete(cns);
            else newSet.add(cns);
            localStorage.setItem('selectedIdosos', JSON.stringify([...newSet]));
            return newSet;
        });
    };

    const toggleAllOnPage = () => {
        const allKeysOnPage = currentItems.map(p => p.cns);
        const allSelected = allKeysOnPage.every(key => selectedPatients.has(key));
        
        setSelectedPatients(prev => {
            const newSet = new Set(prev);
            if (allSelected) allKeysOnPage.forEach(key => newSet.delete(key));
            else allKeysOnPage.forEach(key => newSet.add(key));
            localStorage.setItem('selectedIdosos', JSON.stringify([...newSet]));
            return newSet;
        });
    };

    const clearSelection = () => {
        setSelectedPatients(new Set());
        localStorage.removeItem('selectedIdosos');
    };

    const sendToVisitPage = () => {
        if (selectedPatients.size === 0) {
            alert('Selecione ao menos um idoso!');
            return;
        }

        const selectedData = idosos.filter(p => selectedPatients.has(p.cns));
        
        // Agrupamento por Endereço
        const groups = {};
        selectedData.forEach(p => {
            const key = getAddressKey(p);
            if (!groups[key]) {
                groups[key] = {
                    key: key,
                    addressDisplay: formatAddressDisplay(p),
                    members: []
                };
            }
            groups[key].members.push(p);
        });

        const visitData = Object.values(groups);
        localStorage.setItem('visitQueueData', JSON.stringify(visitData));
        navigate('/folha-visita');
    };

    const allOnPageSelected = currentItems.length > 0 && 
        currentItems.every(p => selectedPatients.has(p.cns));


    return (
                <div className="acs-page">
            <header className="acs-header">
                <div className="acs-header-left">
                    <div className="acs-header-icon-row">
                        <div className="acs-header-icon-box">
                            <Search size={18} color="white" />
                        </div>
                        <h1>Acompanhamento de Idosos (60+)</h1>
                    </div>
                    <p className="acs-header-sub">
                        Gerenciamento de <strong>{filteredIdosos.length}</strong> pacientes
                        {selectedPatients.size > 0 && (
                            <span className="acs-selected-badge">
                                ✓ {selectedPatients.size} selecionado(s)
                            </span>
                        )}
                    </p>
                    <div className="acs-search">
                        <Search size={16} color="rgba(255,255,255,0.7)" />
                        <input
                            type="text"
                            placeholder="Buscar por nome, cartão SUS ou endereço..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
                {!loading && idosos.length > 0 && (
                    <div className="acs-header-right">
                        <MonitoringCharts patients={filteredIdosos} chartConfig={CHART_CONFIG_IDOSOS} />
                    </div>
                )}
            </header>

            <FilterControls
                sortConfig={sortConfig}
                setSortConfig={setSortConfig}
                activeAlerts={activeAlerts}
                setActiveAlerts={setActiveAlerts}
                microAreas={availableMicroAreas}
                selectedMicroArea={selectedMicroArea}
                setSelectedMicroArea={setSelectedMicroArea}
                hiddenFilters={['Idoso', 'Gestante']} 
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
            />

            <div className="acompanhamento-container" style={{ margin: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', fontSize: '0.9rem', color: '#64748b' }}>
                    <div>Exibindo {filteredIdosos.length > 0 ? indexOfFirstItem + 1 : 0}-{Math.min(indexOfLastItem, filteredIdosos.length)} de {filteredIdosos.length}</div>
                    
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        {selectedPatients.size > 0 && (
                            <>
                                <button 
                                    onClick={sendToVisitPage}
                                    style={{
                                        padding: '8px 16px', background: 'linear-gradient(135deg, #059669, #047857)', color: 'white', border: 'none', borderRadius: '6px',
                                        cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px'
                                    }}
                                >
                                    Enviar para Folha de Visita ({selectedPatients.size})
                                </button>
                                <button 
                                    onClick={clearSelection}
                                    className="btn-clear"
                                >
                                    Limpar Seleção
                                </button>
                            </>
                        )}
                        <select 
                            value={itemsPerPage} 
                            onChange={(e) => setItemsPerPage(Number(e.target.value))}
                            className="acs-per-page"
                        >
                            <option value={10}>10 por página</option>
                            <option value={20}>20 por página</option>
                            <option value={50}>50 por página</option>
                        </select>
                    </div>
                </div>

                <div className="acompanhamento-header">
                    {/* Checkbox Header */}
                    <div className="col-checkbox" style={{ width: '50px', display: 'flex', justifyContent: 'center' }}>
                        <div 
                            onClick={toggleAllOnPage}
                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            title="Selecionar todos da página"
                        >
                            {allOnPageSelected && currentItems.length > 0 ? 
                                <Check size={18} color="#059669" strokeWidth={3} /> : 
                                <Square size={18} color="#94a3b8" />
                            }
                        </div>
                    </div>

                    <div className="col-paciente" style={{width: '40%'}}> <User size={14} /> Idoso / Idade </div>
                    <ColumnHeader label="Visitas" icon={User} filterState={getFilterState('visitasACS')} onClick={() => setStatusFilter('visitasACS')} />
                    <ColumnHeader label="Consulta" icon={Stethoscope} filterState={getFilterState('consulta')} onClick={() => setStatusFilter('consulta')} />
                    <ColumnHeader label="Peso/Alt (2x)" icon={Scale} filterState={getFilterState('antropometria')} onClick={() => setStatusFilter('antropometria')} />
                    <ColumnHeader label="Vacina Influenzae" icon={Syringe} filterState={getFilterState('vacinaInfluenza')} onClick={() => setStatusFilter('vacinaInfluenza')} />
                </div>

                {loading ? ( <p style={{ padding: '20px' }}>Carregando...</p> ) : (
                    currentItems.map((patient) => {
                        const isSelected = selectedPatients.has(patient.cns);
                        return (
                            <div 
                                className={`acompanhamento-patient-row${isSelected ? ' selected' : ''}`}
                                key={patient['Cartão SUS'] || patient.cns || Math.random()}
                            >
                                {/* --- COLUNA DE SELEÇÃO ESTILIZADA (AVATAR) --- */}
                                <div className="col-checkbox" style={{ width: '50px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <div 
                                        onClick={() => togglePatientSelection(patient.cns)}
                                        style={{ 
                                            width: '32px', 
                                            height: '32px', 
                                            borderRadius: '50%', 
                                            background: isSelected ? 'linear-gradient(135deg, #059669, #047857)' : '#f1f5f9', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            color: isSelected ? 'white' : '#64748b', 
                                            fontSize: '0.75rem', 
                                            fontWeight: 'bold', 
                                            flexShrink: 0,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            border: isSelected ? '2px solid #065f46' : 'none'
                                        }}
                                        title={isSelected ? "Clique para desmarcar" : "Clique para selecionar"}
                                    >
                                        {isSelected ? <Check size={16} /> : patient.nome.charAt(0)}
                                    </div>
                                </div>

                                <div className="col-paciente" style={{width: '40%', display: 'flex', flexDirection: 'column', gap: '2px'}}> 
                                    <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                                        <span style={{ fontWeight: 600, color: '#334155' }}>{patient.nome}</span>
                                        {patient.status.score !== undefined && (
                                            <span style={{
                                                fontSize: '0.7rem', fontWeight: 'bold', padding: '2px 8px', borderRadius: '12px',
                                                backgroundColor: patient.status.score >= 100 ? '#dcfce7' : (patient.status.score >= 50 ? '#fef3c7' : '#fee2e2'),
                                                color: patient.status.score >= 100 ? '#166534' : (patient.status.score >= 50 ? '#b45309' : '#991b1b'),
                                                border: '1px solid',
                                                borderColor: patient.status.score >= 100 ? '#86efac' : (patient.status.score >= 50 ? '#fcd34d' : '#fecaca')
                                            }}>
                                                {patient.status.score} pts
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.75rem', color: '#64748b' }}>
                                                                            {patient.idade !== -1 && (
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '3px', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>
                                                                                    <Clock size={10} />
                                                                                    <span>{patient.idade} anos</span>
                                                                                </div>
                                                                            )}
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '3px'}}>
                                                                                <MapPin size={10} />
                                                                                <span>
                                                                                    {/* Helper invocado direto no JSX para renderizar o complemento */}
                                                                                    {formatAddressDisplay(patient)} 
                                                                                    {patient.micro_area && ` (MA: ${patient.micro_area})`}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                <div className="col-atividade"><StatusIndicator status={patient.status.visitasACS} /></div>
                                <div className="col-atividade"><StatusIndicator status={patient.status.consulta} /></div>
                                <div className="col-atividade"><StatusIndicator status={patient.status.antropometria} /></div>
                                <div className="col-atividade"><StatusIndicator status={patient.status.vacinaInfluenza} /></div>
                            </div>
                        );
                    })
                )}
                 {!loading && filteredIdosos.length === 0 && (
                    <div className="empty-state">
                        {statusFilter ? "Nenhum idoso com pendência neste indicador." : "Nenhum idoso encontrado."}
                    </div>
                )}
                
                {!loading && filteredIdosos.length > 0 && (
                    <div className="pagination-footer" style={{ display: 'flex', justifyContent: 'center', gap: '10px', padding: '20px', alignItems: 'center', borderTop: '1px solid #e2e8f0', marginTop: 'auto' }}>
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="pagination-btn"><ChevronLeft size={16} /></button>
                        <span style={{ fontSize: '0.9rem' }}>Página <strong>{currentPage}</strong> de <strong>{totalPages}</strong></span>
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="pagination-btn"><ChevronRight size={16} /></button>
                    </div>
                )}
            </div>
        </div>
    );
}